import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Beaker, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  CartesianGrid,
} from 'recharts';
import { DatabaseSchema, LabPanel, LabResultEntry } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onAddLabResult: (entry: LabResultEntry) => Promise<void>;
  onDeleteLabResult: (id: string) => Promise<void>;
}

const PANEL_LABELS: Record<LabPanel, string> = {
  lipids: 'Lipids',
  hba1c: 'Diabetes (glucose / HbA1c)',
  uric_acid: 'Gout (uric acid)',
  vitamin_d: 'Vitamin D',
  thyroid: 'Thyroid',
  kidney: 'Kidney',
  liver: 'Liver & bone',
  fbc: 'Full blood count',
  other: 'Other',
};

const PANEL_ORDER: LabPanel[] = ['lipids', 'hba1c', 'uric_acid', 'vitamin_d', 'kidney', 'liver', 'thyroid', 'fbc', 'other'];

const prettyMarker = (m: string) =>
  m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const flagColor: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  H: 'error',
  L: 'warning',
  normal: 'success',
};

export const LabResults: React.FC<Props> = ({ database, onAddLabResult, onDeleteLabResult }) => {
  const labs = useMemo(() => database?.labResults ?? [], [database?.labResults]);
  const [expandedPanel, setExpandedPanel] = useState<LabPanel | null>('lipids');
  const [expandedMarker, setExpandedMarker] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Partial<LabResultEntry>>({
    date: format(new Date(), 'yyyy-MM-dd'),
    panel: 'lipids',
    marker: '',
    unit: '',
    cadence: 'annual',
  });

  const byPanel = useMemo(() => {
    const groups: Record<string, LabResultEntry[]> = {};
    labs.forEach((l) => {
      if (!groups[l.panel]) groups[l.panel] = [];
      groups[l.panel].push(l);
    });
    return groups;
  }, [labs]);

  const handleAdd = async () => {
    if (!form.marker || !form.unit || form.value === undefined) return;
    await onAddLabResult({
      id: `lab-${Date.now()}`,
      date: form.date || format(new Date(), 'yyyy-MM-dd'),
      panel: (form.panel as LabPanel) || 'other',
      marker: form.marker,
      value: Number(form.value),
      unit: form.unit,
      referenceLow: form.referenceLow ? Number(form.referenceLow) : undefined,
      referenceHigh: form.referenceHigh ? Number(form.referenceHigh) : undefined,
      flag: form.flag,
      cadence: form.cadence ?? 'annual',
      notes: form.notes,
      source: 'manual',
    });
    setAdding(false);
    setForm({ date: format(new Date(), 'yyyy-MM-dd'), panel: 'lipids', marker: '', unit: '', cadence: 'annual' });
  };

  const renderMarkerTrend = (panel: LabPanel, marker: string) => {
    const points = labs
      .filter((l) => l.panel === panel && l.marker === marker)
      .sort((a, b) => a.date.localeCompare(b.date));
    if (!points.length) return null;
    const ref = points[points.length - 1];
    const chartData = points.map((p) => ({ date: format(parseISO(p.date), 'MMM yyyy'), value: p.value }));
    return (
      <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
        {points.length === 1 ? (
          <Typography variant="caption" color="text.secondary">
            One reading so far — future results will build a trend line here.
          </Typography>
        ) : (
          <Box sx={{ width: '100%', height: 160 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                {ref.referenceLow !== undefined && ref.referenceHigh !== undefined && (
                  <ReferenceArea y1={ref.referenceLow} y2={ref.referenceHigh} fill="#10b981" fillOpacity={0.08} />
                )}
                <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        {points.map((p) => (
          <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Typography variant="caption">
              {format(parseISO(p.date), 'd MMM yyyy')} · <strong>{p.value} {p.unit}</strong>
              {p.referenceLow !== undefined || p.referenceHigh !== undefined ? (
                <span style={{ color: '#888' }}>
                  {' '}(ref {p.referenceLow ?? '−∞'}–{p.referenceHigh ?? '∞'})
                </span>
              ) : null}
              {p.flag && <Chip label={p.flag} size="small" color={flagColor[p.flag] ?? 'default'} sx={{ ml: 1, height: 18 }} />}
            </Typography>
            <IconButton size="small" onClick={() => onDeleteLabResult(p.id)}>
              <Trash2 size={14} />
            </IconButton>
          </Box>
        ))}
      </Box>
    );
  };

  const lastMeasured = (panel: LabPanel) => {
    const group = byPanel[panel];
    if (!group?.length) return null;
    const latest = group.reduce((acc, l) => (l.date > acc.date ? l : acc), group[0]);
    return formatDistanceToNow(parseISO(latest.date), { addSuffix: true });
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Beaker size={22} />
              <Typography variant="h6" fontWeight={700}>Lab results</Typography>
            </Stack>
            <Button size="small" startIcon={<Plus size={16} />} onClick={() => setAdding((v) => !v)}>
              {adding ? 'Cancel' : 'Add result'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Annual panels — update after each blood test. No nudges.
          </Typography>

          <Collapse in={adding}>
            <Stack spacing={1.5} sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField label="Date" type="date" size="small" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
                <TextField select label="Panel" size="small" value={form.panel} onChange={(e) => setForm({ ...form, panel: e.target.value as LabPanel })} fullWidth>
                  {PANEL_ORDER.map((p) => <MenuItem key={p} value={p}>{PANEL_LABELS[p]}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField label="Marker (e.g. hba1c)" size="small" value={form.marker} onChange={(e) => setForm({ ...form, marker: e.target.value })} fullWidth />
                <TextField label="Unit" size="small" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} fullWidth />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField label="Value" type="number" size="small" value={form.value ?? ''} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} fullWidth />
                <TextField label="Ref low" type="number" size="small" value={form.referenceLow ?? ''} onChange={(e) => setForm({ ...form, referenceLow: e.target.value ? Number(e.target.value) : undefined })} fullWidth />
                <TextField label="Ref high" type="number" size="small" value={form.referenceHigh ?? ''} onChange={(e) => setForm({ ...form, referenceHigh: e.target.value ? Number(e.target.value) : undefined })} fullWidth />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField select label="Flag" size="small" value={form.flag ?? ''} onChange={(e) => setForm({ ...form, flag: (e.target.value || undefined) as any })} fullWidth>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="H">High</MenuItem>
                  <MenuItem value="L">Low</MenuItem>
                </TextField>
                <TextField select label="Cadence" size="small" value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value as any })} fullWidth>
                  <MenuItem value="annual">Annual</MenuItem>
                  <MenuItem value="frequent">Frequent</MenuItem>
                </TextField>
              </Stack>
              <Button variant="contained" onClick={handleAdd}>Save result</Button>
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {PANEL_ORDER.filter((p) => byPanel[p]?.length).map((panel) => {
        const markers = Array.from(new Set(byPanel[panel].map((l) => l.marker)));
        const hasFlagged = byPanel[panel].some((l) => l.flag === 'H' || l.flag === 'L');
        const isOpen = expandedPanel === panel;
        return (
          <Card key={panel} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setExpandedPanel(isOpen ? null : panel)}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <Typography variant="subtitle1" fontWeight={700}>{PANEL_LABELS[panel]}</Typography>
                  {hasFlagged && <Chip label="flagged" size="small" color="warning" />}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  last: {lastMeasured(panel)}
                </Typography>
              </Box>

              <Collapse in={isOpen}>
                <Box sx={{ mt: 2 }}>
                  {markers.map((m) => {
                    const latest = byPanel[panel]
                      .filter((l) => l.marker === m)
                      .reduce((acc, l) => (l.date > acc.date ? l : acc));
                    const key = `${panel}:${m}`;
                    return (
                      <Box
                        key={m}
                        sx={{
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setExpandedMarker(expandedMarker === key ? null : key)}
                        >
                          <Typography variant="body2" fontWeight={600}>{prettyMarker(m)}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">
                              {latest.value} {latest.unit}
                            </Typography>
                            {latest.flag && <Chip label={latest.flag} size="small" color={flagColor[latest.flag] ?? 'default'} />}
                          </Stack>
                        </Stack>
                        {expandedMarker === key && renderMarkerTrend(panel, m)}
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};
